import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { billsAPI, receiptsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import { format } from 'date-fns';

export default function Dashboard() {
  const { settings } = useSettings();
  const [stats, setStats] = useState({
    total: 0, unpaid: 0, paid: 0, partial: 0, revenue: 0, recentBills: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      billsAPI.getAll({ limit: 5 }),
      billsAPI.getAll({ status: 'unpaid', limit: 100 }),
      billsAPI.getAll({ status: 'paid', limit: 100 }),
      billsAPI.getAll({ status: 'partial', limit: 100 }),
    ]).then(([all, unpaid, paid, partial]) => {
      const revenue =
        paid.data.bills.reduce((s, b) => s + parseFloat(b.total), 0) +
        partial.data.bills.reduce((s, b) => s + parseFloat(b.total), 0);
      setStats({
        total: all.data.total,
        unpaid: unpaid.data.total,
        paid: paid.data.total,
        partial: partial.data.total,
        revenue,
        recentBills: all.data.bills,
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cur = settings.currency || 'BDT';

  const statCards = [
    { label: 'Total Bills', value: stats.total, icon: '🧾', bg: '#dbeafe' },
    { label: 'Unpaid', value: stats.unpaid, icon: '⏳', bg: '#fef2f2' },
    { label: 'Partial', value: stats.partial, icon: '🔄', bg: '#fffbeb' },
    { label: 'Paid', value: stats.paid, icon: '✅', bg: '#ecfdf5' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Dashboard</h1>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/bills/new" className="btn btn-primary">+ New Bill</Link>
          <Link to="/receipts/new" className="btn btn-outline">+ Receipt</Link>
        </div>
      </div>

      {/* Revenue Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 60%, #0ea5e9 100%)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 20, color: '#fff',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: .8, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Total Revenue
          </div>
          <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 32, fontWeight: 700, marginTop: 4 }}>
            {cur} {stats.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div style={{ fontSize: 44, opacity: .25 }}>💰</div>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid">
        {statCards.map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-icon" style={{ background: s.bg }}>
              <span>{s.icon}</span>
            </div>
            <div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">{loading ? '...' : s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Bills */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recent Bills</span>
          <Link to="/bills" className="btn btn-outline btn-sm">View All</Link>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : stats.recentBills.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🧾</div>
            <div className="empty-state-text">No bills yet</div>
            <Link to="/bills/new" className="btn btn-primary" style={{ marginTop: 16 }}>Create First Bill</Link>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="table-wrapper desktop-table">
              <table>
                <thead>
                  <tr>
                    <th>Bill No.</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentBills.map(b => (
                    <tr key={b.id}>
                      <td><span style={{ fontWeight: 600, color: '#2563eb' }}>{b.bill_number}</span></td>
                      <td>{b.client_name}</td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>
                        {b.bill_date ? format(new Date(b.bill_date), 'dd MMM yyyy') : '-'}
                      </td>
                      <td style={{ fontWeight: 600 }}>{cur} {parseFloat(b.total).toLocaleString()}</td>
                      <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                      <td><Link to={`/bills/${b.id}`} className="btn btn-outline btn-sm">View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-card-list" style={{ padding: '12px' }}>
              {stats.recentBills.map(b => (
                <div className="mobile-card" key={b.id}>
                  <div className="mobile-card-row">
                    <span style={{ fontWeight: 700, color: '#2563eb', fontSize: 14 }}>{b.bill_number}</span>
                    <span className={`badge badge-${b.status}`}>{b.status}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Client</span>
                    <span className="mobile-card-value">{b.client_name}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Date</span>
                    <span className="mobile-card-value">
                      {b.bill_date ? format(new Date(b.bill_date), 'dd MMM yyyy') : '-'}
                    </span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Amount</span>
                    <span className="mobile-card-value" style={{ fontWeight: 700 }}>
                      {cur} {parseFloat(b.total).toLocaleString()}
                    </span>
                  </div>
                  <div className="mobile-card-actions">
                    <Link to={`/bills/${b.id}`} className="btn btn-outline btn-sm">View Bill</Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}